import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AutoReboost from '../components/AutoReboost';

const API = (path, opts = {}) => {
  const token = localStorage.getItem('token');
  return fetch(path, { ...opts, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...opts.headers } });
};

const Dashboard = ({ user, onLogout }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'home';
  const [tab, setTab] = useState(initialTab);
  const [orders, setOrders] = useState([]);
  const [topups, setTopups] = useState([]);
  const [profile, setProfile] = useState(user || {});
  const [stock, setStock] = useState({ boost_1m: 0, boost_3m: 0 });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Buy form
  const [buyInvite, setBuyInvite] = useState('');
  const [buyProduct, setBuyProduct] = useState('boost_3m');
  const [buyQty, setBuyQty] = useState(14);
  const [buyCrypto, setBuyCrypto] = useState('LTC');
  const [buyMethod, setBuyMethod] = useState('balance');
  const [buyMsg, setBuyMsg] = useState(null);
  const [buyLoading, setBuyLoading] = useState(false);
  const [lastOrderId, setLastOrderId] = useState(null);

  // Topup form
  const [topAmt, setTopAmt] = useState(10);
  const [topCrypto, setTopCrypto] = useState('LTC');
  const [topPayMethod, setTopPayMethod] = useState('crypto');
  const [topMsg, setTopMsg] = useState(null);
  const [topLoading, setTopLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState({ stripe: false, paypal: false, crypto: true });

  // Referral
  const [referralData, setReferralData] = useState(null);
  const [refCopied, setRefCopied] = useState(false);

  // Tickets
  const [tickets, setTickets] = useState([]);
  const [ticketForm, setTicketForm] = useState({ subject: '', message: '', orderId: '' });
  const [ticketMsg, setTicketMsg] = useState(null);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [viewTicket, setViewTicket] = useState(null);
  const [replyText, setReplyText] = useState('');

  // Review incentive
  const [reviewPrompt, setReviewPrompt] = useState(null);
  const [reviewForm, setReviewForm] = useState({ stars: 5, text: '' });
  const [reviewMsg, setReviewMsg] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewOrderId, setReviewOrderId] = useState('');
  const [reviewCheckLoading, setReviewCheckLoading] = useState(false);
  
  // User coupons
  const [userCoupons, setUserCoupons] = useState([]);

  // API key
  const [showApiKey, setShowApiKey] = useState(false);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);
  const [regenLoading, setRegenLoading] = useState(false);
  const [regenMsg, setRegenMsg] = useState(null);

  // Tool licenses
  const [toolLicenses, setToolLicenses] = useState([]);
  const [toolProducts, setToolProducts] = useState([]);
  const [toolLoading, setToolLoading] = useState(false);
  const [toolMsg, setToolMsg] = useState(null);
  const [redeemForm, setRedeemForm] = useState({ key: '', productId: '' });
  const [redeemLoading, setRedeemLoading] = useState(false);

  // Guides
  const [docsSection, setDocsSection] = useState('welcome');
  // Sidebar mobile
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Dashboard updates (from admin)
  const [dashUpdates, setDashUpdates] = useState([]);

  const loadProfile = useCallback(async () => {
    try {
      const res = await API('/api/auth/me');
      if (!res.ok) { onLogout(); return; }
      const data = await res.json();
      setProfile(data);
    } catch { onLogout(); }
  }, [onLogout]);

  const loadOrders = useCallback(async () => {
    try {
      const res = await API('/api/user/orders');
      if (res.ok) setOrders(await res.json());
    } catch {}
  }, []);

  const loadTopups = useCallback(async () => {
    try {
      const res = await API('/api/user/topups');
      if (res.ok) setTopups(await res.json());
    } catch {}
  }, []);

  const [productPrices, setProductPrices] = useState({ boost_1m: 0.20, boost_3m: 0.25 });
  const [allProducts, setAllProducts] = useState([]);

  const loadStock = useCallback(async () => {
    try {
      const res = await fetch('/api/get-stock');
      if (res.ok) {
        const data = await res.json();
        const s = {};
        const pp = {};
        data.forEach(p => { s[p.id] = p.stock; pp[p.id] = p.pricePerBoost; });
        setStock(s);
        setProductPrices(prev => ({ ...prev, ...pp }));
        setAllProducts(data);
      }
    } catch {}
  }, []);

  const loadToolLicenses = useCallback(async () => {
    try {
      const res = await API('/api/user/tool-licenses');
      if (res.ok) setToolLicenses(await res.json());
    } catch {}
  }, []);

  const loadToolProducts = useCallback(async () => {
    try {
      const res = await fetch('/api/tool-products');
      if (res.ok) setToolProducts(await res.json());
    } catch {}
  }, []);

  const loadReferral = useCallback(async () => {
    try {
      const res = await API('/api/user/referral');
      if (res.ok) setReferralData(await res.json());
    } catch {}
  }, []);

  const loadTickets = useCallback(async () => {
    try {
      const res = await API('/api/user/tickets');
      if (res.ok) setTickets(await res.json());
    } catch {}
  }, []);

  const loadCoupons = useCallback(async () => {
    try {
      const res = await API('/api/user/my-coupons');
      if (res.ok) setUserCoupons(await res.json());
    } catch {}
  }, []);

  const checkReviewEligibility = useCallback(async (ordersList) => {
    // Check latest completed order for review eligibility
    const completed = (ordersList || orders).filter(o => o.boostStatus === 'completed' && !o.isTopup);
    if (completed.length === 0) return;
    const latest = completed[0];
    try {
      const res = await API(`/api/user/can-review/${latest.id}`);
      if (res.ok) {
        const data = await res.json();
        if (data.canReview) setReviewPrompt({ orderId: latest.id, discount: data.discount });
      }
    } catch {}
  }, [orders]);

  useEffect(() => {
    loadProfile();
    loadOrders();
    loadTopups();
    loadStock();
    loadReferral();
    loadTickets();
    loadCoupons();
    loadToolLicenses();
    loadToolProducts();
    // Load payment methods
    fetch('/api/payment-methods').then(r => r.json()).then(data => {
      setPaymentMethods(data);
      if (data.stripe) setTopPayMethod('stripe');
      else if (data.crypto) setTopPayMethod('crypto');
    }).catch(() => {});
    fetch('/api/dashboard-updates').then(r => r.json()).then(d => { if (Array.isArray(d)) setDashUpdates(d); }).catch(() => {});
    const i = setInterval(() => { loadProfile(); loadOrders(); loadStock(); }, 10000);
    return () => clearInterval(i);
  }, [loadProfile, loadOrders, loadTopups, loadStock, loadReferral, loadTickets, loadCoupons, loadToolLicenses, loadToolProducts]);

  // Check review eligibility when orders load
  useEffect(() => {
    if (orders.length > 0) checkReviewEligibility(orders);
  }, [orders, checkReviewEligibility]);

  // Listen for nav-tab events from the Navbar dropdown
  useEffect(() => {
    const handleNavTab = (e) => { if (e.detail) setTab(e.detail); };
    window.addEventListener('nav-tab', handleNavTab);
    return () => window.removeEventListener('nav-tab', handleNavTab);
  }, []);

  // Clean up URL query param after reading initial tab
  useEffect(() => {
    if (searchParams.get('tab')) {
      searchParams.delete('tab');
      setSearchParams(searchParams, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Check any order ID for review eligibility
  const checkOrderForReview = async () => {
    if (!reviewOrderId.trim()) return;
    setReviewCheckLoading(true);
    setReviewMsg(null);
    try {
      const res = await fetch(`/api/check-review-eligible/${reviewOrderId.trim()}`);
      const data = await res.json();
      
      if (!data.eligible) {
        setReviewMsg({ type: 'err', text: data.reason || 'Order not eligible for review.' });
        return;
      }
      
      // Order is eligible - show review form
      setReviewPrompt({ orderId: reviewOrderId.trim(), discount: data.discount || 5 });
      setReviewMsg({ type: 'ok', text: 'Order found! Leave your review below.' });
    } catch (err) {
      setReviewMsg({ type: 'err', text: 'Failed to check order. Please try again.' });
    } finally {
      setReviewCheckLoading(false);
    }
  };

  const handleBuy = async (e) => {
    e.preventDefault();
    setBuyMsg(null);
    setBuyLoading(true);
    const isTokenBuy = buyProduct.startsWith('token_');
    try {
      const endpoint = buyMethod === 'balance' ? '/api/user/buy-with-balance' : '/api/user/create-order';
      const isToolBuy = buyProduct.startsWith('tool_');
      const body = { serverInvite: (isTokenBuy || isToolBuy) ? '' : buyInvite, productId: buyProduct, quantity: buyQty };
      if (buyMethod !== 'balance') body.cryptoType = buyCrypto;
      const res = await API(endpoint, { method: 'POST', body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      if (buyMethod === 'balance') {
        setLastOrderId(data.orderId);
        setBuyMsg({ type: 'ok', text: isTokenBuy ? 'Order placed! Tokens are being delivered.' : 'Order placed! Boosts are being delivered.' });
        loadProfile(); loadOrders(); loadStock();
      } else {
        window.open(`/checkout/${data.orderId}`, '_blank');
        setBuyMsg({ type: 'ok', text: 'Checkout opened in new tab' });
        loadOrders();
      }
    } catch (err) {
      setBuyMsg({ type: 'err', text: err.message });
    } finally { setBuyLoading(false); }
  };

  const handleTopup = async (e) => {
    e.preventDefault();
    setTopMsg(null);
    
    // Validate minimum for Stripe
    if (topPayMethod === 'stripe' && topAmt < 1) {
      setTopMsg({ type: 'err', text: 'Minimum top-up for card payment is $1.00' });
      return;
    }
    
    setTopLoading(true);
    try {
      if (topPayMethod === 'stripe') {
        // Create Stripe checkout for top-up
        const res = await fetch('/api/create-stripe-topup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: JSON.stringify({ amount: topAmt })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error);
        if (data.url) {
          window.location.href = data.url;
        } else {
          throw new Error('Failed to create checkout');
        }
      } else {
        // Crypto top-up (existing flow)
        const res = await API('/api/user/topup', { method: 'POST', body: JSON.stringify({ amount: topAmt, cryptoType: topCrypto }) });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error);
        window.open(`/checkout/${data.orderId}`, '_blank');
        setTopMsg({ type: 'ok', text: 'Checkout opened — balance will update after payment' });
        loadTopups();
      }
    } catch (err) {
      setTopMsg({ type: 'err', text: err.message });
    } finally { setTopLoading(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    onLogout();
    navigate('/');
  };

  const greet = () => {
    const h = new Date().getHours();
    return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
  };

  const maskEmail = (e) => {
    if (!e) return '';
    const [name, domain] = e.split('@');
    return name.slice(0, 3) + '***@' + domain;
  };

  const total = (buyQty * (productPrices[buyProduct] || 0.25)).toFixed(2);
  const isBuyToken = buyProduct.startsWith('token_');

  const statusBadge = (s) => {
    const cls = s === 'completed' ? 'db-bg-ok' : s === 'failed' ? 'db-bg-err' : s === 'confirmed' ? 'db-bg-ok' : s === 'processing' ? 'db-bg-info' : 'db-bg-warn';
    return <span className={`db-badge ${cls}`}>{s || 'pending'}</span>;
  };

  const switchTab = (t) => { setTab(t); setSidebarOpen(false); };
  const NavBtn = ({ id, icon, label, badge }) => (
    <button className={`dbn ${tab === id ? 'on' : ''}`} onClick={() => switchTab(id)}>
      {icon}<span>{label}</span>
      {badge && <span className="dbn-badge">{badge}</span>}
    </button>
  );

  return (
    <div className="db-layout">
      {/* Mobile hamburger */}
      <button className="db-hamburger" onClick={() => setSidebarOpen(!sidebarOpen)}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
      </button>
      {sidebarOpen && <div className="db-overlay" onClick={() => setSidebarOpen(false)} />}

      {/* ═══ Sidebar ═══ */}
      <aside className={`db-sidebar2 ${sidebarOpen ? 'open' : ''}`}>
        <div className="dbs-header">
          <a href="/" className="dbs-logo">
            <img src="/logo.png" alt="" style={{width:22,height:22,borderRadius:5,objectFit:'contain'}} />
            <span>Outsourced</span>
          </a>
          <span className="db-bal-pill2">${(profile.balance || 0).toFixed(2)}</span>
        </div>

        <nav className="dbs-nav">
          <div className="dbs-label">Main</div>
          <NavBtn id="home" icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><path d="M9 22V12h6v10"/></svg>} label="Dashboard" />
          <NavBtn id="redeem" icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.778 7.778 5.5 5.5 0 017.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>} label="Redeem Key" />
          <NavBtn id="downloads" icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><path d="M7 10l5 5 5-5"/><path d="M12 15V3"/></svg>} label="Downloads" />
          <NavBtn id="guides" icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M4 19.5A2.5 2.5 0 004 22h16V2H6.5A2.5 2.5 0 004 4.5v15z"/></svg>} label="Guides" />

          <div className="dbs-label">Shop</div>
          <NavBtn id="buy" icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>} label="Purchase" />
          <NavBtn id="orders" icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>} label="Orders" />
          <NavBtn id="reboost" icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>} label="Auto-Reboost" badge="NEW" />
          <NavBtn id="addbal" icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>} label="Add Balance" />
          <NavBtn id="topups" icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>} label="Top Up History" badge={topups.filter(t=>t.status==='waiting').length > 0 ? topups.filter(t=>t.status==='waiting').length : null} />

          <div className="dbs-label">Account</div>
          <NavBtn id="api" icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>} label="API Access" />
          <NavBtn id="tools" icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>} label="My Tools" />
          <NavBtn id="referral" icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>} label="Referrals" />
          <NavBtn id="tickets" icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 18v-6a9 9 0 0118 0v6"/><path d="M21 19a2 2 0 01-2 2h-1a2 2 0 01-2-2v-3a2 2 0 012-2h3z"/><path d="M3 19a2 2 0 002 2h1a2 2 0 002-2v-3a2 2 0 00-2-2H3z"/></svg>} label="Support" badge={tickets.filter(t=>t.status==='replied').length > 0 ? tickets.filter(t=>t.status==='replied').length : null} />
        </nav>

        <div className="dbs-footer">
          <div className="dbs-user">
            <div className="dbs-avatar">{(profile.email || '?')[0].toUpperCase()}</div>
            <div><div className="dbs-username">{profile.email?.split('@')[0] || 'User'}</div><div className="dbs-useremail">{maskEmail(profile.email)}</div></div>
          </div>
          <button className="dbs-logout" onClick={handleLogout}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          </button>
        </div>
      </aside>

      {/* ═══ Main ═══ */}
      <div className="db-body2">
        {/* Mobile Tab Bar */}
        <div className="db-mobile-tabs">
          <button className={`db-mtab ${tab==='home'?'on':''}`} onClick={()=>setTab('home')}>Home</button>
          <button className={`db-mtab ${tab==='buy'?'on':''}`} onClick={()=>setTab('buy')}>Buy</button>
          <button className={`db-mtab ${tab==='redeem'?'on':''}`} onClick={()=>setTab('redeem')}>Redeem</button>
          <button className={`db-mtab ${tab==='orders'?'on':''}`} onClick={()=>setTab('orders')}>Orders</button>
          <button className={`db-mtab ${tab==='addbal'?'on':''}`} onClick={()=>setTab('addbal')}>Top Up</button>
          <button className={`db-mtab ${tab==='tools'?'on':''}`} onClick={()=>setTab('tools')}>Tools</button>
          <button className={`db-mtab ${tab==='tickets'?'on':''}`} onClick={()=>setTab('tickets')}>Support</button>
        </div>

        <div className="db-main">

          {/* HOME TAB */}
          {tab === 'home' && (
            <>
              <h1 className="db-greeting">Overview, <span style={{ color: 'var(--accent)' }}>{profile.email?.split('@')[0] || 'User'}</span></h1>
              <p className="db-sub">Manage your tools, downloads, and guides from one dashboard.</p>

              {/* Stats Row */}
              <div className="db-stats-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginTop: 20 }}>
                <div className="db-stat-card">
                  <div className="db-stat-label">Balance</div>
                  <div className="db-stat-val accent">${(profile.balance || 0).toFixed(2)}</div>
                </div>
                <div className="db-stat-card">
                  <div className="db-stat-label">Total Spent</div>
                  <div className="db-stat-val">${(profile.totalSpent || 0).toFixed(2)}</div>
                </div>
                <div className="db-stat-card">
                  <div className="db-stat-label">Orders</div>
                  <div className="db-stat-val">{orders.filter(o => !o.isTopup).length}</div>
                </div>
                <div className="db-stat-card">
                  <div className="db-stat-label">Active Licenses</div>
                  <div className="db-stat-val accent">{toolLicenses.length}</div>
                </div>
              </div>

              {/* Shortcuts */}
              <div className="db-section-head"><h2>Shortcuts</h2></div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                {[
                  { id: 'redeem', label: 'Redeem a Key', desc: 'Activate your products', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.778 7.778 5.5 5.5 0 017.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg> },
                  { id: 'downloads', label: 'Downloads', desc: 'Grab installers & updates', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><path d="M7 10l5 5 5-5"/><path d="M12 15V3"/></svg> },
                  { id: 'guides', label: 'Guides', desc: 'How-tos & best practices', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M4 19.5A2.5 2.5 0 004 22h16V2H6.5A2.5 2.5 0 004 4.5v15z"/></svg> },
                  { id: 'tickets', label: 'Support', desc: 'Join our Discord', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 18v-6a9 9 0 0118 0v6"/><path d="M21 19a2 2 0 01-2 2h-1a2 2 0 01-2-2v-3a2 2 0 012-2h3z"/><path d="M3 19a2 2 0 002 2h1a2 2 0 002-2v-3a2 2 0 00-2-2H3z"/></svg> },
                ].map(s => (
                  <button key={s.id} onClick={() => setTab(s.id)} className="db-shortcut-card">
                    <div className="db-shortcut-icon">{s.icon}</div>
                    <div className="db-shortcut-label">{s.label}</div>
                    <div className="db-shortcut-desc">{s.desc}</div>
                  </button>
                ))}
              </div>

              {/* Main grid: Updates (left) + Status/Get Started/Recent Orders (right) */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 20 }}>
                {/* Latest Updates */}
                <div className="db-card" style={{ padding: '1.25rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                    <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'rgba(46,232,197,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Latest updates</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Fresh changes and improvements</div>
                    </div>
                  </div>
                  <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                    {dashUpdates.length === 0 ? (
                      <div style={{ padding: '24px 0', fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>No updates yet.</div>
                    ) : dashUpdates.slice(0, 5).map((upd, i) => (
                      <div key={i} style={{ padding: '12px 0', borderTop: i > 0 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{upd.title}</div>
                          <div style={{ fontSize: 10, color: 'var(--text-muted)', flexShrink: 0, marginLeft: 8 }}>{upd.date ? new Date(upd.date).toLocaleDateString() : ''}</div>
                        </div>
                        {upd.lines && upd.lines.map((line, j) => (
                          <div key={j} style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6, paddingLeft: 2 }}>– {line}</div>
                        ))}
                        {upd.body && (!upd.lines || !upd.lines.length) && (
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{upd.body}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right column */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {/* System status */}
                  <div className="db-card" style={{ padding: '1rem 1.25rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>System status</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#2ee8c5', boxShadow: '0 0 6px rgba(46,232,197,0.5)' }}></div>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>All systems operational</span>
                      </div>
                    </div>
                  </div>

                  {/* Get started */}
                  <div className="db-card" style={{ padding: '1.25rem' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12 }}>Get started</div>
                    {[
                      { label: 'Redeem your first key', link: 'Go to Redeem', tab2: 'redeem' },
                      { label: 'Download the launcher', link: 'Open Downloads', tab2: 'downloads' },
                      { label: 'Read the quick start guide', link: 'View Guides', tab2: 'guides' },
                    ].map((item, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderTop: i > 0 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{item.label}</span>
                        <button onClick={() => setTab(item.tab2)} style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 12, cursor: 'pointer', padding: 0, fontFamily: 'var(--font-primary)', fontWeight: 600 }}>{item.link} ›</button>
                      </div>
                    ))}
                  </div>

                  {/* Recent Orders */}
                  <div className="db-card" style={{ padding: '1.25rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Recent orders</div>
                      <button className="db-link-btn" onClick={() => setTab('orders')}>View all ›</button>
                    </div>
                    {orders.filter(o => !o.isTopup).length === 0 ? (
                      <div style={{ padding: '16px 0', fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>No orders yet</div>
                    ) : orders.filter(o => !o.isTopup).slice(0, 4).map((o, i) => (
                      <div key={o.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderTop: i > 0 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary)' }}>{o.productName || '—'}</div>
                          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{o.createdAt ? new Date(o.createdAt).toLocaleDateString() : ''}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>${(o.total || 0).toFixed(2)}</div>
                          {statusBadge(o.boostStatus || o.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Review section */}
              <div className="db-section-head" style={{ marginTop: '2rem' }}>
                <h2>Leave a Review</h2>
              </div>
              
              <div className="db-card" style={{ padding: '1.25rem' }}>
                {!reviewPrompt ? (
                  <>
                    <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                      Enter your order ID to leave a review and get a <strong style={{ color: 'var(--accent)' }}>5% discount</strong> coupon!
                    </p>
                    <div style={{ marginBottom: '0.75rem' }}>
                      <input 
                        type="text" 
                        placeholder="Paste your full Order ID here..." 
                        value={reviewOrderId}
                        onChange={e => setReviewOrderId(e.target.value)}
                        style={{ 
                          width: '100%', 
                          padding: '1rem 1rem',
                          fontSize: '0.9rem',
                          background: 'rgba(255,255,255,0.03)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '10px',
                          color: 'var(--text-primary)',
                          outline: 'none',
                          fontFamily: 'monospace',
                          boxSizing: 'border-box',
                          marginBottom: '0.75rem'
                        }}
                      />
                      <button 
                        onClick={checkOrderForReview}
                        disabled={reviewCheckLoading || !reviewOrderId.trim()}
                        style={{ 
                          width: '100%',
                          padding: '0.85rem 1.5rem', 
                          background: reviewOrderId.trim() ? 'var(--accent)' : 'rgba(255,255,255,0.05)',
                          border: 'none',
                          borderRadius: '10px',
                          color: reviewOrderId.trim() ? '#000' : 'var(--text-muted)',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          cursor: reviewOrderId.trim() ? 'pointer' : 'not-allowed'
                        }}
                      >
                        {reviewCheckLoading ? 'Checking...' : 'Find Order'}
                      </button>
                    </div>
                    {reviewMsg && (
                      <div style={{ 
                        padding: '0.75rem', 
                        borderRadius: '8px', 
                        fontSize: '0.8rem',
                        background: reviewMsg.type === 'ok' ? 'rgba(6,244,173,0.1)' : 'rgba(240,72,95,0.1)',
                        color: reviewMsg.type === 'ok' ? 'var(--accent)' : '#f0485f',
                        border: `1px solid ${reviewMsg.type === 'ok' ? 'rgba(6,244,173,0.2)' : 'rgba(240,72,95,0.2)'}`
                      }}>{reviewMsg.text}</div>
                    )}
                  </>
                ) : (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                      <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: 0 }}>
                        Reviewing order: <code style={{ color: 'var(--accent)' }}>{reviewPrompt.orderId.slice(0, 13)}...</code>
                      </p>
                      <button 
                        onClick={() => { setReviewPrompt(null); setReviewMsg(null); setReviewForm({ stars: 5, text: '' }); }}
                        style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.75rem' }}
                      >
                        ✕ Cancel
                      </button>
                    </div>
                    
                    <div style={{ marginBottom: '0.75rem' }}>
                      <label style={{ fontSize: '0.72rem', color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>Rating</label>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        {[1, 2, 3, 4, 5].map(s => (
                          <button
                            key={s}
                            type="button"
                            onClick={() => setReviewForm({ ...reviewForm, stars: s })}
                            style={{
                              background: 'none',
                              border: 'none',
                              fontSize: '1.4rem',
                              cursor: 'pointer',
                              color: s <= reviewForm.stars ? '#f5a623' : 'rgba(255,255,255,0.15)',
                              padding: '0 2px'
                            }}
                          >
                            ★
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <div style={{ marginBottom: '0.75rem' }}>
                      <label style={{ fontSize: '0.72rem', color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>Your Review</label>
                      <textarea
                        value={reviewForm.text}
                        onChange={e => setReviewForm({ ...reviewForm, text: e.target.value })}
                        className="db-input"
                        rows="3"
                        placeholder="Share your experience... (min 10 characters)"
                        style={{ resize: 'none', fontSize: '0.8rem', width: '100%', boxSizing: 'border-box' }}
                      />
                    </div>
                    
                    <button
                      className="db-submit"
                      disabled={reviewLoading || reviewForm.text.length < 10}
                      onClick={async () => {
                        setReviewLoading(true);
                        setReviewMsg(null);
                        try {
                          const token = localStorage.getItem('token');
                          const res = await fetch('/api/submit-review', { 
                            method: 'POST', 
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ 
                              ...reviewForm, 
                              orderId: reviewPrompt.orderId,
                              token: token
                            }) 
                          });
                          const text = await res.text();
                          let data;
                          try {
                            data = JSON.parse(text);
                          } catch {
                            throw new Error('Server error. Please try again.');
                          }
                          if (!res.ok) throw new Error(data.error || 'Failed to submit');
                          setReviewMsg({ type: 'ok', text: data.message || 'Review submitted!' });
                          setReviewPrompt(null);
                          setReviewForm({ stars: 5, text: '' });
                          setReviewOrderId('');
                          loadProfile();
                          loadCoupons();
                        } catch (err) {
                          setReviewMsg({ type: 'err', text: err.message });
                        } finally {
                          setReviewLoading(false);
                        }
                      }}
                      style={{ width: '100%', padding: '0.6rem' }}
                    >
                      {reviewLoading ? 'Submitting...' : 'Submit Review & Get 5% Coupon'}
                    </button>
                    
                    {reviewMsg && (
                      <div className={`db-msg ${reviewMsg.type}`} style={{ marginTop: '0.75rem', fontSize: '0.75rem' }}>{reviewMsg.text}</div>
                    )}
                  </>
                )}
              </div>
              
              {/* My Coupons Section */}
              {userCoupons.length > 0 && (
                <>
                  <div className="db-section-head" style={{ marginTop: '2rem' }}>
                    <h2>🎟️ My Coupons</h2>
                  </div>
                  <div className="db-card" style={{ padding: '1.25rem' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {userCoupons.map((coupon, idx) => (
                        <div 
                          key={idx}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '0.75rem 1rem',
                            background: coupon.used ? 'rgba(255,255,255,0.02)' : 'rgba(6,244,173,0.05)',
                            border: `1px solid ${coupon.used ? 'rgba(255,255,255,0.05)' : 'rgba(6,244,173,0.15)'}`,
                            borderRadius: '10px'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <code style={{ 
                              fontSize: '0.9rem', 
                              fontWeight: 600,
                              color: coupon.used ? 'var(--text-muted)' : 'var(--accent)',
                              textDecoration: coupon.used ? 'line-through' : 'none'
                            }}>
                              {coupon.code}
                            </code>
                            <span style={{ 
                              fontSize: '0.75rem',
                              padding: '0.2rem 0.5rem',
                              borderRadius: '4px',
                              background: coupon.used ? 'rgba(255,255,255,0.05)' : 'rgba(6,244,173,0.15)',
                              color: coupon.used ? 'var(--text-muted)' : 'var(--accent)'
                            }}>
                              {coupon.discount}% OFF
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            {coupon.used ? (
                              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>✓ Used</span>
                            ) : (
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(coupon.code);
                                  alert('Coupon copied to clipboard!');
                                }}
                                style={{
                                  padding: '0.35rem 0.75rem',
                                  fontSize: '0.7rem',
                                  background: 'var(--accent)',
                                  color: '#000',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontWeight: 600
                                }}
                              >
                                Copy
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {/* ORDERS TAB */}
          {tab === 'orders' && (
            <>
              <h1 className="db-greeting">Order History</h1>
              <p className="db-sub">All orders associated with your account</p>
              <div className="db-divider"></div>
              <div className="db-table-wrap">
                <table className="db-table">
                  <thead>
                    <tr><th>Order ID</th><th>Product</th><th>Qty</th><th>Amount</th><th>Payment</th><th>Status</th><th>Date</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {orders.filter(o => !o.isTopup).length === 0 ? (
                      <tr><td colSpan="8" className="db-empty">No orders yet</td></tr>
                    ) : orders.filter(o => !o.isTopup).map(o => (
                      <tr key={o.id}>
                        <td className="db-mono" style={{ fontSize: '0.7rem', maxWidth: '180px', wordBreak: 'break-all' }}>{o.id}</td>
                        <td>{o.productId?.startsWith('token_') ? '🎟️ ' : ''}{o.productName || '—'}</td>
                        <td>{o.quantity || 0}</td>
                        <td>${(o.total || 0).toFixed(2)}</td>
                        <td>{o.paymentMethod === 'stripe' ? 'Card' : o.paymentMethod === 'paypal' ? 'PayPal' : o.paidWithBalance ? 'Balance' : o.cryptoType}</td>
                        <td>{statusBadge(o.boostStatus)}</td>
                        <td className="db-muted">{o.createdAt ? new Date(o.createdAt).toLocaleDateString() : ''}</td>
                        <td>
                          {o.status === 'waiting' && !o.paidWithBalance && (
                            <a href={`/checkout/${o.id}`} target="_blank" rel="noreferrer" className="db-action-btn">Pay</a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* TOPUPS TAB */}
          {tab === 'topups' && (
            <>
              <h1 className="db-greeting">Top Up History</h1>
              <p className="db-sub">Balance deposits and their status</p>
              <div className="db-divider"></div>
              <div className="db-table-wrap">
                <table className="db-table">
                  <thead>
                    <tr><th>ID</th><th>Amount</th><th>Payment</th><th>Status</th><th>Date</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {topups.length === 0 ? (
                      <tr><td colSpan="6" className="db-empty">No top-ups yet</td></tr>
                    ) : topups.map(t => (
                      <tr key={t.id}>
                        <td className="db-mono">{t.id.slice(0,8)}…</td>
                        <td>${(t.total || 0).toFixed(2)}</td>
                        <td>{t.paymentMethod === 'stripe' ? 'Card' : t.cryptoType || 'Crypto'}</td>
                        <td>{statusBadge(t.status)}</td>
                        <td className="db-muted">{t.createdAt ? new Date(t.createdAt).toLocaleDateString() : ''}</td>
                        <td>
                          {t.status === 'waiting' && (
                            <a href={`/checkout/${t.id}`} target="_blank" rel="noreferrer" className="db-action-btn">Pay</a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* ADD BALANCE TAB */}
          {tab === 'addbal' && (
            <>
              <h1 className="db-greeting">Add Balance</h1>
              <p className="db-sub">Top up your account</p>
              <div className="db-divider"></div>
              <div className="db-form-card">
                <form onSubmit={handleTopup}>
                  <div className="db-field">
                    <label>Amount (USD)</label>
                    <div className="db-amount-grid">
                      {[5, 10, 25, 50, 100].map(a => (
                        <button type="button" key={a} className={`db-amt-btn ${topAmt===a?'on':''}`} onClick={()=>setTopAmt(a)}>${a}</button>
                      ))}
                    </div>
                    <input type="number" value={topAmt} onChange={e=>setTopAmt(parseFloat(e.target.value)||0)} min="1" max="1000" step="0.01" className="db-input" />
                  </div>
                  <div className="db-field">
                    <label>Payment Method</label>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                      {paymentMethods.stripe && (
                        <button type="button" 
                          className={`db-amt-btn ${topPayMethod==='stripe'?'on':''}`} 
                          onClick={()=>setTopPayMethod('stripe')}
                          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                            <line x1="1" y1="10" x2="23" y2="10"/>
                          </svg>
                          Card
                        </button>
                      )}
                      {paymentMethods.crypto && (
                        <button type="button" 
                          className={`db-amt-btn ${topPayMethod==='crypto'?'on':''}`} 
                          onClick={()=>setTopPayMethod('crypto')}
                          style={{ flex: 1 }}>
                          Crypto
                        </button>
                      )}
                    </div>
                  </div>
                  {topPayMethod === 'crypto' && (
                    <div className="db-field">
                      <label>Cryptocurrency</label>
                      <select value={topCrypto} onChange={e=>setTopCrypto(e.target.value)} className="db-input">
                        <option value="LTC">Litecoin (LTC)</option>
                        <option value="BTC">Bitcoin (BTC)</option>
                        <option value="ETH">Ethereum (ETH)</option>
                        <option value="USDT_ERC20">USDT ERC20</option>
                        <option value="USDC_ERC20">USDC ERC20</option>
                      </select>
                    </div>
                  )}
                  {topPayMethod === 'stripe' && topAmt < 5 && (
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px' }}>
                      +$0.50 card processing fee for orders under $5
                    </div>
                  )}
                  <button type="submit" className="db-submit" disabled={topLoading}>
                    {topLoading ? 'Creating...' : `Top Up $${(topAmt + (topPayMethod === 'stripe' && topAmt < 5 ? 0.50 : 0)).toFixed(2)}`}
                  </button>
                  {topMsg && <div className={`db-msg ${topMsg.type}`}>{topMsg.text}</div>}
                </form>
              </div>
            </>
          )}

          {/* BUY TAB */}
          {tab === 'buy' && (
            <>
              <h1 className="db-greeting">{isBuyToken ? 'Buy Nitro Tokens' : 'Buy Discord Server Boosts'}</h1>
              <p className="db-sub">{isBuyToken ? 'Purchase tokens using your balance or cryptocurrency' : 'Purchase boosts using your balance or cryptocurrency'}</p>
              <div className="db-divider"></div>
              <div className="db-form-card">
                <form onSubmit={handleBuy}>
                  {!isBuyToken && (
                  <div className="db-field">
                    <label>Server Invite</label>
                    <input type="text" value={buyInvite} onChange={e=>setBuyInvite(e.target.value)} placeholder="https://discord.gg/..." className="db-input" required={!isBuyToken} />
                  </div>
                  )}
                  <div className="db-row2">
                    <div className="db-field">
                      <label>Amount</label>
                      <div className="db-stepper">
                        <button type="button" className="db-step-btn" onClick={()=>setBuyQty(Math.max(isBuyToken ? 1 : 2, buyQty-(isBuyToken ? 1 : 2)))}>−</button>
                        <input type="number" value={buyQty} onChange={e=>setBuyQty(Math.max(isBuyToken ? 1 : 2, parseInt(e.target.value)||(isBuyToken ? 1 : 2)))} className="db-step-input" min={isBuyToken ? 1 : 2} step={isBuyToken ? 1 : 2} />
                        <button type="button" className="db-step-btn" onClick={()=>setBuyQty(buyQty+(isBuyToken ? 1 : 2))}>+</button>
                      </div>
                      <span className="db-field-hint">1 quantity = 1x {isBuyToken ? 'token' : 'boost'}</span>
                      {!isBuyToken && buyQty % 2 !== 0 && <span className="db-field-warn">⚠ Boost orders require even quantities (2, 4, 6, etc.)</span>}
                    </div>
                    <div className="db-field">
                      <label>Product</label>
                      <select value={buyProduct} onChange={e=>{setBuyProduct(e.target.value);if(e.target.value.startsWith('token_'))setBuyQty(1);else setBuyQty(14);}} className="db-input">
                        <option value="boost_3m">3 Month Server Boost</option>
                        <option value="boost_1m">1 Month Server Boost</option>
                        {allProducts.filter(p => p.type === 'token').map(p => (
                          <option key={p.id} value={p.id}>🎟️ {p.product}</option>
                        ))}
                      </select>
                      <span className="db-field-hint db-stock">{stock[buyProduct] || 0} in stock</span>
                    </div>
                  </div>
                  <div className="db-field">
                    <label>Payment Method</label>
                    <div className="db-method-grid">
                      <button type="button" className={`db-method-btn ${buyMethod==='balance'?'on':''}`} onClick={()=>setBuyMethod('balance')}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
                        Balance (${(profile.balance||0).toFixed(2)})
                      </button>
                      <button type="button" className={`db-method-btn ${buyMethod==='crypto'?'on':''}`} onClick={()=>setBuyMethod('crypto')}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v12M9 9h4.5a2.5 2.5 0 0 1 0 5H9"/></svg>
                        Cryptocurrency
                      </button>
                    </div>
                  </div>
                  {buyMethod === 'crypto' && (
                    <div className="db-field">
                      <label>Crypto</label>
                      <select value={buyCrypto} onChange={e=>setBuyCrypto(e.target.value)} className="db-input">
                        <option value="LTC">Litecoin (LTC)</option>
                        <option value="BTC">Bitcoin (BTC)</option>
                        <option value="ETH">Ethereum (ETH)</option>
                        <option value="USDT_ERC20">USDT ERC20</option>
                        <option value="USDC_ERC20">USDC ERC20</option>
                      </select>
                    </div>
                  )}
                  <div className="db-price-box">
                    <div className="db-price-row"><span>Subtotal:</span><span>${total}</span></div>
                    <div className="db-price-row db-price-total"><span>Total:</span><span>${total}</span></div>
                  </div>
                  <button type="submit" className="db-submit" disabled={buyLoading || (!isBuyToken && buyQty % 2 !== 0) || (stock[buyProduct] || 0) < buyQty}>
                    {buyLoading ? 'Processing...' : isBuyToken ? 'Purchase Tokens' : 'Purchase Boosts'}
                  </button>
                  {(stock[buyProduct] || 0) < buyQty && (stock[buyProduct] || 0) > 0 && (
                    <div className="db-msg err">Not enough stock. Only {stock[buyProduct]} available.</div>
                  )}
                  {buyMsg && (
                    <div className={`db-msg ${buyMsg.type}`}>
                      {buyMsg.text}
                      {lastOrderId && buyMsg.type === 'ok' && (
                        <div className="db-order-id">
                          <span>Order ID: </span>
                          <code>{lastOrderId}</code>
                          <button type="button" className="db-copy-btn" onClick={()=>{navigator.clipboard.writeText(lastOrderId);}}>Copy</button>
                        </div>
                      )}
                    </div>
                  )}
                </form>
              </div>
            </>
          )}

          {/* AUTO-REBOOST TAB */}
          {tab === 'reboost' && (
            <AutoReboost
              balance={profile.balance || 0}
              onBalanceChange={loadProfile}
            />
          )}

          {/* REFERRAL TAB */}
          {tab === 'referral' && (
            <>
              <h1 className="db-greeting">Referral Program</h1>
              <p className="db-sub">Earn {10}% bonus balance when friends you refer make purchases</p>
              <div className="db-divider"></div>
              
              <div className="db-stats-row">
                <div className="db-stat-card">
                  <div className="db-stat-label">Your Code</div>
                  <div className="db-stat-val accent">{referralData?.code || profile.referralCode || '...'}</div>
                </div>
                <div className="db-stat-card">
                  <div className="db-stat-label">Total Earnings</div>
                  <div className="db-stat-val">${(referralData?.earnings || 0).toFixed(2)}</div>
                </div>
                <div className="db-stat-card">
                  <div className="db-stat-label">People Referred</div>
                  <div className="db-stat-val">{referralData?.count || 0}</div>
                </div>
              </div>

              <div className="db-form-card">
                <h3 style={{fontSize:'0.85rem',marginBottom:'0.6rem'}}>Share your referral link</h3>
                <div style={{display:'flex',gap:'0.5rem',alignItems:'center'}}>
                  <input type="text" readOnly value={`${window.location.origin}/register?ref=${referralData?.code || profile.referralCode || ''}`} className="db-input" style={{flex:1,fontSize:'0.75rem',fontFamily:'monospace'}} />
                  <button type="button" className="db-copy-btn" style={{padding:'8px 16px',fontSize:'0.75rem'}} onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/register?ref=${referralData?.code || profile.referralCode || ''}`); setRefCopied(true); setTimeout(()=>setRefCopied(false), 2000); }}>
                    {refCopied ? '✓ Copied' : 'Copy'}
                  </button>
                </div>
                <p style={{fontSize:'0.7rem',color:'var(--text-muted)',marginTop:'0.5rem'}}>When someone signs up with your code and makes a purchase, you'll receive 10% of their order total as balance credit.</p>
              </div>

              {referralData?.referrals?.length > 0 && (
                <>
                  <div className="db-section-head" style={{marginTop:'1.2rem'}}>Referred Users</div>
                  <div className="db-table-wrap">
                    <table className="db-table">
                      <thead><tr><th>Email</th><th>Date</th></tr></thead>
                      <tbody>
                        {referralData.referrals.map((r, i) => (
                          <tr key={i}>
                            <td>{r.email}</td>
                            <td className="db-muted">{new Date(r.date).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}

          {/* TICKETS TAB */}

          {/* API ACCESS TAB */}
          {tab === 'api' && (
            <>
              <h1 className="db-greeting">API Access</h1>
              <p className="db-sub">Automate boost orders with our REST API</p>
              <div className="db-divider"></div>

              <div className="db-form-card" style={{ maxWidth: '100%' }}>
                <div className="db-field">
                  <label>Your API Key</label>
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <input type={showApiKey ? 'text' : 'password'} value={profile.apiKey || ''} readOnly className="db-input" style={{ flex: 1, fontFamily: 'monospace', fontSize: '0.75rem' }} />
                    <button type="button" className="db-copy-btn" style={{ padding: '0.5rem 0.75rem', fontSize: '0.72rem', whiteSpace: 'nowrap' }} onClick={() => setShowApiKey(!showApiKey)}>
                      {showApiKey ? 'Hide' : 'Show'}
                    </button>
                    <button type="button" className="db-copy-btn" style={{ padding: '0.5rem 0.75rem', fontSize: '0.72rem' }} onClick={() => { navigator.clipboard.writeText(profile.apiKey); setApiKeyCopied(true); setTimeout(() => setApiKeyCopied(false), 2000); }}>
                      {apiKeyCopied ? '✓ Copied' : 'Copy'}
                    </button>
                  </div>
                  <span className="db-field-hint">Include as <code style={{ background: 'rgba(255,255,255,0.04)', padding: '1px 4px', borderRadius: 3, fontSize: '0.68rem' }}>X-Api-Key</code> header in all requests, or use <code style={{ background: 'rgba(255,255,255,0.04)', padding: '1px 4px', borderRadius: 3, fontSize: '0.68rem' }}>Authorization: Bearer your_token</code></span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem' }}>
                  <button type="button" className="db-submit" style={{ background: 'rgba(240,72,95,0.15)', color: '#f0485f', border: '1px solid rgba(240,72,95,0.3)', padding: '0.5rem 1rem', fontSize: '0.75rem' }} disabled={regenLoading} onClick={async () => {
                    if (!window.confirm('Are you sure? Your current API key will stop working immediately.')) return;
                    setRegenLoading(true); setRegenMsg(null);
                    try {
                      const res = await API('/api/user/regenerate-key', { method: 'POST' });
                      const data = await res.json();
                      if (!res.ok) throw new Error(data.error);
                      setProfile(prev => ({ ...prev, apiKey: data.apiKey }));
                      setRegenMsg({ type: 'ok', text: 'API key regenerated successfully!' });
                    } catch (err) { setRegenMsg({ type: 'err', text: err.message }); }
                    finally { setRegenLoading(false); }
                  }}>
                    {regenLoading ? 'Regenerating...' : 'Regenerate Key'}
                  </button>
                  {regenMsg && <span style={{ fontSize: '0.75rem', color: regenMsg.type === 'ok' ? 'var(--accent)' : 'var(--red-primary)' }}>{regenMsg.text}</span>}
                </div>
              </div>

              <div style={{ marginTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {[
                  {
                    method: 'GET', path: '/api/get-stock', title: 'Get Products & Stock',
                    desc: 'Returns available products with real-time stock levels and pricing. No auth required.',
                    response: `[{
  "id": "boost_1m",
  "product": "1 Month",
  "stock": 2598,
  "pricePerBoost": ${productPrices.boost_1m || 0.20},
  "boostOptions": [2, 8, 14, 20, 30]
}]`
                  },
                  {
                    method: 'POST', path: '/api/user/buy-with-balance', title: 'Buy with Balance',
                    desc: 'Place a boost order using your account balance. Boosts begin processing immediately.',
                    body: `{
  "serverInvite": "discord.gg/yourserver",
  "productId": "boost_1m",
  "quantity": 14
}`,
                    response: `{
  "success": true,
  "orderId": "abc-123-...",
  "order": {
    "id": "abc-123-...",
    "productName": "1 Month",
    "quantity": 14,
    "total": 2.80,
    "status": "confirmed",
    "boostStatus": "processing"
  }
}`
                  },
                  {
                    method: 'POST', path: '/api/user/create-order', title: 'Create Crypto Order',
                    desc: 'Create an order with a crypto checkout page. Returns a checkout URL for payment.',
                    body: `{
  "serverInvite": "discord.gg/yourserver",
  "productId": "boost_1m",
  "quantity": 14,
  "cryptoType": "LTC"
}`,
                    response: `{
  "success": true,
  "orderId": "abc-123-...",
  "checkoutUrl": "/checkout/abc-123-..."
}`
                  },
                  {
                    method: 'GET', path: '/api/user/orders', title: 'List My Orders',
                    desc: 'Get all your orders sorted by most recent.',
                    response: `[{
  "id": "abc-123",
  "productName": "1 Month",
  "quantity": 14,
  "total": 2.80,
  "boostStatus": "completed",
  "createdAt": 1706900000000
}]`
                  },
                  {
                    method: 'GET', path: '/api/order/:orderId', title: 'Get Order Status',
                    desc: 'Get detailed order info including payment and boost status. Public endpoint — no auth required.',
                    response: `{
  "id": "abc-123",
  "productName": "1 Month",
  "quantity": 14,
  "total": 2.80,
  "boostStatus": "completed",
  "createdAt": 1706900000000
}`
                  },
                  {
                    method: 'POST', path: '/api/user/regenerate-key', title: 'Regenerate API Key',
                    desc: 'Generate a new API key. Your old key stops working immediately.',
                    response: `{
  "success": true,
  "apiKey": "usr_newabc123..."
}`
                  }
                ].map((ep, i) => (
                  <div key={i} className="db-form-card" style={{ maxWidth: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <span className={`db-badge ${ep.method === 'GET' ? 'db-bg-ok' : 'db-bg-warn'}`} style={{ fontFamily: 'monospace', fontWeight: 600, letterSpacing: '0.5px' }}>{ep.method}</span>
                      <code style={{ fontSize: '0.78rem', color: 'var(--text-primary)' }}>{ep.path}</code>
                    </div>
                    <h3 style={{ fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.25rem' }}>{ep.title}</h3>
                    <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.65rem' }}>{ep.desc}</p>

                    {ep.body && (
                      <div className="db-field" style={{ marginBottom: '0.65rem' }}>
                        <label>Request Body</label>
                        <pre style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 7, padding: '0.75rem', fontSize: '0.72rem', color: 'var(--orb-orange, #f5a623)', overflow: 'auto', fontFamily: 'monospace', margin: 0, lineHeight: 1.5 }}>{ep.body}</pre>
                      </div>
                    )}
                    <div className="db-field" style={{ marginBottom: 0 }}>
                      <label>Response</label>
                      <pre style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 7, padding: '0.75rem', fontSize: '0.72rem', color: 'var(--accent)', overflow: 'auto', fontFamily: 'monospace', margin: 0, lineHeight: 1.5 }}>{ep.response}</pre>
                    </div>
                  </div>
                ))}

                <div className="db-form-card" style={{ maxWidth: '100%' }}>
                  <h3 style={{ fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.5rem' }}>Example (cURL)</h3>
                  <pre style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 7, padding: '0.75rem', fontSize: '0.72rem', color: 'var(--text-primary)', overflow: 'auto', fontFamily: 'monospace', lineHeight: 1.6, margin: 0 }}>
{`curl -X POST ${window.location.origin}/api/user/buy-with-balance \\
  -H "Content-Type: application/json" \\
  -H "X-Api-Key: ${showApiKey ? (profile.apiKey || 'your_api_key') : 'your_api_key'}" \\
  -d '{"serverInvite":"discord.gg/abc","productId":"boost_1m","quantity":14}'`}
                  </pre>
                </div>

                <div style={{ padding: '0.75rem 1rem', borderRadius: '8px', background: 'rgba(0,229,208,0.04)', border: '1px solid rgba(0,229,208,0.1)', display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
                  <span style={{ color: 'var(--accent)', fontSize: '0.9rem', marginTop: '1px' }}>📖</span>
                  <p style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>
                    <strong style={{ color: 'var(--text-primary)' }}>Full Documentation:</strong> Check out the <a href="/docs" style={{ color: 'var(--accent)', textDecoration: 'none' }}>Tool Docs</a> for setup guides, configuration help, and requirements for all tools.
                  </p>
                </div>
              </div>
            </>
          )}

          {tab === 'tickets' && (
            <>
              <h1 className="db-greeting">Support Tickets</h1>
              <p className="db-sub">Need help? Open a ticket and we'll get back to you</p>
              <div className="db-divider"></div>

              {!viewTicket ? (
                <>
                  {/* New Ticket Form */}
                  <div className="db-form-card" style={{marginBottom:'1.5rem'}}>
                    <h3 style={{fontSize:'0.85rem',fontWeight:500,marginBottom:'0.75rem'}}>New Ticket</h3>
                    <div className="db-field">
                      <label>Subject</label>
                      <input type="text" value={ticketForm.subject} onChange={e=>setTicketForm({...ticketForm,subject:e.target.value})} className="db-input" placeholder="Brief description of your issue" required />
                    </div>
                    <div className="db-field">
                      <label>Order ID (optional)</label>
                      <select value={ticketForm.orderId} onChange={e=>setTicketForm({...ticketForm,orderId:e.target.value})} className="db-input">
                        <option value="">None</option>
                        {orders.filter(o=>!o.isTopup).map(o=>(
                          <option key={o.id} value={o.id}>{o.id.slice(0,8)}… — {o.productName}</option>
                        ))}
                      </select>
                    </div>
                    <div className="db-field">
                      <label>Message</label>
                      <textarea value={ticketForm.message} onChange={e=>setTicketForm({...ticketForm,message:e.target.value})} className="db-input" rows="4" placeholder="Describe your issue in detail..." style={{resize:'vertical'}} required />
                    </div>
                    <button className="db-submit" disabled={ticketLoading||!ticketForm.subject||!ticketForm.message} onClick={async()=>{
                      setTicketLoading(true);setTicketMsg(null);
                      try{
                        const res=await API('/api/user/tickets',{method:'POST',body:JSON.stringify(ticketForm)});
                        const data=await res.json();
                        if(!res.ok)throw new Error(data.error);
                        setTicketMsg({type:'ok',text:`Ticket ${data.ticketId} created! We'll respond soon.`});
                        setTicketForm({subject:'',message:'',orderId:''});
                        loadTickets();
                      }catch(err){setTicketMsg({type:'err',text:err.message});}
                      finally{setTicketLoading(false);}
                    }}>
                      {ticketLoading?'Submitting...':'Submit Ticket'}
                    </button>
                    {ticketMsg && <div className={`db-msg ${ticketMsg.type}`}>{ticketMsg.text}</div>}
                  </div>

                  {/* Ticket List */}
                  {tickets.length > 0 && (
                    <>
                      <div className="db-section-head"><h2>Your Tickets</h2></div>
                      <div className="db-table-wrap">
                        <table className="db-table">
                          <thead><tr><th>ID</th><th>Subject</th><th>Status</th><th>Messages</th><th>Updated</th><th></th></tr></thead>
                          <tbody>
                            {tickets.map(t=>(
                              <tr key={t.id} style={{cursor:'pointer'}} onClick={()=>setViewTicket(t)}>
                                <td className="db-mono">{t.id}</td>
                                <td><strong>{t.subject}</strong></td>
                                <td>{statusBadge(t.status==='replied'?'completed':t.status==='closed'?'failed':t.status)}</td>
                                <td>{t.messages?.length||0}</td>
                                <td className="db-muted">{t.updatedAt?new Date(t.updatedAt).toLocaleDateString():''}</td>
                                <td><button className="db-action-btn" onClick={e=>{e.stopPropagation();setViewTicket(t);}}>View</button></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <>
                  {/* Ticket Detail */}
                  <button className="db-action-btn" style={{marginBottom:'1rem'}} onClick={()=>{setViewTicket(null);setReplyText('');}}>← Back to tickets</button>
                  <div className="db-form-card" style={{maxWidth:'100%'}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'0.75rem'}}>
                      <div>
                        <h3 style={{fontSize:'0.88rem',fontWeight:500}}>{viewTicket.subject}</h3>
                        <span style={{fontSize:'0.7rem',color:'var(--text-muted)',fontFamily:'monospace'}}>{viewTicket.id}</span>
                      </div>
                      {statusBadge(viewTicket.status==='replied'?'completed':viewTicket.status==='closed'?'failed':viewTicket.status)}
                    </div>
                    <div style={{display:'flex',flexDirection:'column',gap:'0.5rem',marginBottom:'1rem',maxHeight:350,overflowY:'auto'}}>
                      {(viewTicket.messages||[]).map((m,i)=>(
                        <div key={i} style={{
                          background:m.from==='admin'?'rgba(6,244,173,0.04)':'rgba(255,255,255,0.02)',
                          border:`1px solid ${m.from==='admin'?'rgba(6,244,173,0.1)':'rgba(255,255,255,0.05)'}`,
                          borderRadius:8,padding:'0.65rem 0.85rem'
                        }}>
                          <div style={{display:'flex',justifyContent:'space-between',marginBottom:'0.3rem'}}>
                            <span style={{fontSize:'0.72rem',fontWeight:600,color:m.from==='admin'?'var(--accent)':'var(--text-primary)'}}>{m.name||m.from}</span>
                            <span style={{fontSize:'0.65rem',color:'var(--text-muted)'}}>{new Date(m.timestamp).toLocaleString()}</span>
                          </div>
                          <div style={{fontSize:'0.8rem',color:'var(--text-secondary)',whiteSpace:'pre-wrap'}}>{m.text}</div>
                        </div>
                      ))}
                    </div>
                    {viewTicket.status !== 'closed' && (
                      <div>
                        <textarea value={replyText} onChange={e=>setReplyText(e.target.value)} className="db-input" rows="3" placeholder="Type your reply..." style={{resize:'vertical',marginBottom:'0.5rem'}} />
                        <button className="db-submit" disabled={!replyText.trim()} onClick={async()=>{
                          try{
                            const res=await API(`/api/user/tickets/${viewTicket.id}/reply`,{method:'POST',body:JSON.stringify({message:replyText})});
                            if(res.ok){
                              setReplyText('');
                              await loadTickets();
                              const updated=await(await API(`/api/user/tickets/${viewTicket.id}`)).json();
                              setViewTicket(updated);
                            }
                          }catch{}
                        }}>Send Reply</button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          {tab === 'tools' && (
            <>
              <h1 className="db-greeting">My Tools</h1>
              <p className="db-sub">Manage your active licenses</p>
              <div className="db-divider"></div>

              {toolLicenses.length === 0 ? (
                <div className="db-card" style={{ padding: '3rem', textAlign: 'center' }}>
                  <div style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(46,232,197,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', color: 'var(--accent)' }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>No licenses yet</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>Purchase a tool to get started, or redeem a key.</div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                    <button className="db-submit" style={{ width: 'auto', padding: '10px 24px' }} onClick={() => setTab('buy')}>Browse Tools</button>
                    <button className="db-copy-btn" style={{ padding: '10px 24px' }} onClick={() => setTab('redeem')}>Redeem Key</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {toolLicenses.map((lic, i) => (
                    <div key={i} className="db-tool-card">
                      <div className="db-tool-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div className="db-tool-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>
                          </div>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{lic.productName}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                              Added {new Date(lic.createdAt).toLocaleDateString()}
                              {lic.expiresAt && <> · Expires {new Date(lic.expiresAt).toLocaleDateString()}</>}
                            </div>
                          </div>
                        </div>
                        <span className={`db-badge ${lic.redeemed ? 'db-bg-ok' : 'db-bg-warn'}`}>
                          {lic.redeemed ? 'Active' : 'Not Redeemed'}
                        </span>
                      </div>

                      <div className="db-tool-key">
                        <code>{lic.key}</code>
                        <button onClick={() => { navigator.clipboard.writeText(lic.key); setToolMsg('Key copied!'); setTimeout(() => setToolMsg(null), 2000); }} className="db-tool-copy">Copy</button>
                      </div>

                      <div className="db-tool-actions">
                        <button 
                          onClick={async () => {
                            if (!window.confirm(`Reset HWID for this license? (${lic.hwidResets || 0}/${lic.hwidResetLimit || '∞'} used)`)) return;
                            setToolLoading(true); setToolMsg(null);
                            try {
                              const res = await API('/api/user/tool-reset-hwid', { method: 'POST', body: JSON.stringify({ licenseKey: lic.key }) });
                              const data = await res.json();
                              if (data.success) { setToolMsg(`HWID reset! (${data.resetsUsed}/${data.resetLimit || '∞'})`); loadToolLicenses(); }
                              else setToolMsg(data.error || 'Failed');
                            } catch (e) { setToolMsg(e.message); }
                            setToolLoading(false);
                          }}
                          disabled={toolLoading}
                          className="db-tool-btn warn"
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
                          Reset HWID {lic.hwidResetLimit > 0 ? `(${lic.hwidResets || 0}/${lic.hwidResetLimit})` : ''}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {toolMsg && (
                <div style={{ padding: '10px 14px', background: toolMsg.includes('success') || toolMsg.includes('copied') || toolMsg.includes('reset') ? 'rgba(46,232,197,.08)' : 'rgba(240,72,95,.08)', border: `1px solid ${toolMsg.includes('success') || toolMsg.includes('copied') || toolMsg.includes('reset') ? 'rgba(46,232,197,.15)' : 'rgba(240,72,95,.15)'}`, borderRadius: 8, fontSize: 12, color: toolMsg.includes('success') || toolMsg.includes('copied') || toolMsg.includes('reset') ? 'var(--accent, #2ee8c5)' : '#f0485f', marginTop: 14 }}>
                  {toolMsg}
                </div>
              )}
            </>
          )}

          {/* ═══ REDEEM KEY TAB ═══ */}
          {tab === 'redeem' && (
            <>
              <div className="db-breadcrumb">
                <span className="db-bc-step active">1</span> Select tool
                <span className="db-bc-sep">›</span>
                <span className="db-bc-step active">2</span> Enter key
                <span className="db-bc-sep">›</span>
                <span className="db-bc-step">3</span> Redeem
              </div>
              <h1 className="db-greeting">Redeem your <span style={{ color: 'var(--accent)' }}>Product Key</span></h1>
              <p className="db-sub">Activate your tools in seconds, {profile.email?.split('@')[0] || 'User'}.</p>

              <div className="db-redeem-grid">
                <div className="db-form-card" style={{ padding: '2rem 2.5rem' }}>
                  <div className="db-field" style={{ marginBottom: 20 }}>
                    <label style={{ fontSize: 14, marginBottom: 8, display: 'block' }}>Tool</label>
                    <select value={redeemForm.productId} onChange={e => setRedeemForm(p => ({ ...p, productId: e.target.value }))} className="db-input" style={{ padding: '14px 14px', fontSize: 14 }}>
                      <option value="">Choose a tool...</option>
                      {toolProducts.map(tp => <option key={tp.id} value={tp.id}>{tp.name}</option>)}
                    </select>
                  </div>
                  <div className="db-field" style={{ marginBottom: 20 }}>
                    <label style={{ fontSize: 14, marginBottom: 8, display: 'block' }}>Product key</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input placeholder="XXXX-XXXX-XXXX-XXXX" value={redeemForm.key} onChange={e => setRedeemForm(p => ({ ...p, key: e.target.value }))} className="db-input" style={{ flex: 1, fontFamily: 'monospace', padding: '14px 14px', fontSize: 14, letterSpacing: '1px' }} />
                      <button className="db-copy-btn" style={{ padding: '14px 18px', fontSize: 13 }} onClick={() => navigator.clipboard.readText().then(t => setRedeemForm(p => ({ ...p, key: t })))}>Paste</button>
                      <button className="db-copy-btn" style={{ padding: '14px 18px', fontSize: 13 }} onClick={() => setRedeemForm(p => ({ ...p, key: '' }))}>Clear</button>
                    </div>
                    <span className="db-field-hint" style={{ marginTop: 8 }}>Any length is accepted. Enter exactly as received.</span>
                  </div>
                  <button className="db-submit" style={{ padding: '16px', fontSize: 15, marginTop: 8 }} disabled={redeemLoading || !redeemForm.productId || !redeemForm.key} onClick={async () => {
                    if (!redeemForm.productId || !redeemForm.key) { setToolMsg('Select a tool and enter a key'); return; }
                    setRedeemLoading(true); setToolMsg(null);
                    try {
                      const res = await API('/api/user/tool-redeem', { method: 'POST', body: JSON.stringify(redeemForm) });
                      const data = await res.json();
                      if (data.success) { setToolMsg('Key redeemed successfully!'); setRedeemForm({ key: '', productId: '' }); loadToolLicenses(); }
                      else setToolMsg(data.error || 'Failed to redeem');
                    } catch (e) { setToolMsg(e.message); }
                    setRedeemLoading(false);
                  }}>
                    {redeemLoading ? 'Redeeming...' : 'Redeem key'}
                  </button>
                  {toolMsg && <div className={`db-msg ${toolMsg.includes('success') ? 'ok' : 'err'}`} style={{ marginTop: 12 }}>{toolMsg}</div>}
                </div>

                <div className="db-redeem-side">
                  <div className="db-card" style={{ padding: '1.25rem' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
                      <span style={{ color: 'var(--accent)' }}>ⓘ</span> Need help?
                    </div>
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6, margin: '0 0 12px' }}>We're around 24/7 to help with activation issues.</p>
                    <button className="db-copy-btn" style={{ width: '100%', textAlign: 'center', padding: '8px 16px' }} onClick={() => setTab('tickets')}>Open Support Ticket</button>
                  </div>
                  <div className="db-card" style={{ padding: '1.25rem' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Quick tips</div>
                    {['Keys can be any length — enter them exactly as shown.', 'Each key generally redeems once per account.', 'Trouble? Paste directly, then try again.'].map((tip, i) => (
                      <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
                        <span style={{ color: 'var(--accent)', marginTop: 1 }}>•</span>{tip}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ═══ DOWNLOADS TAB ═══ */}
          {tab === 'downloads' && (
            <>
              <h1 className="db-greeting">Downloads</h1>
              <p className="db-sub">Download your purchased tools</p>
              <div className="db-divider"></div>
              {toolLicenses.length === 0 ? (
                <div className="db-card" style={{ padding: '3rem', textAlign: 'center' }}>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No tools purchased yet. Buy a tool to see downloads here.</div>
                  <button className="db-submit" style={{ marginTop: 16, width: 'auto', display: 'inline-flex' }} onClick={() => setTab('buy')}>Browse Tools</button>
                </div>
              ) : (
                [...new Map(toolLicenses.map(l => [l.productId, l])).values()].map((lic, i) => (
                  <div key={i} className="db-card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                      <div style={{ width: 42, height: 42, borderRadius: 10, background: 'rgba(46,232,197,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', flexShrink: 0 }}>
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><path d="M7 10l5 5 5-5"/><path d="M12 15V3"/></svg>
                      </div>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{lic.productName}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>License: {lic.key.slice(0, 12)}…</div>
                      </div>
                    </div>
                    {lic.downloadUrl ? (
                      <a href={lic.downloadUrl} target="_blank" rel="noopener noreferrer" className="db-submit" style={{ width: 'auto', padding: '10px 24px', fontSize: 13, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}>Download</a>
                    ) : (
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>No download available</span>
                    )}
                  </div>
                ))
              )}
            </>
          )}

          {/* ═══ GUIDES TAB ═══ */}
          {tab === 'guides' && (() => {
            const I = {
              gear: <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
              book: <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" /></svg>,
              bot: <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" /></svg>,
              bolt: <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" /></svg>,
              search: <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" /></svg>,
              refresh: <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" /></svg>,
              ext: <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" /></svg>,
            };
            return (
            <>
              <h1 className="db-greeting">Guides & Documentation</h1>
              <p className="db-sub">Setup guides for all your tools and licenses</p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 24 }}>
                {[
                  { icon: I.bot, name: 'Boost Bot', desc: 'Discord bot for automated server boosting', color: 'var(--accent)' },
                  { icon: I.bolt, name: 'Boost Tool', desc: 'Standalone console boosting tool', color: 'var(--orange)' },
                  { icon: I.search, name: 'Token Checker', desc: 'Check token validity & boost slots', color: 'var(--purple)' },
                  { icon: I.refresh, name: 'Token Changer', desc: 'Bulk change token profiles', color: 'var(--cyan)' },
                ].map((t, i) => (
                  <a key={i} href="/docs" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <div className="db-stat-card" style={{ cursor: 'pointer', padding: 20, transition: 'border-color 0.15s' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                        <div style={{ color: t.color, display: 'flex' }}>{t.icon}</div>
                        <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{t.name}</span>
                      </div>
                      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 12 }}>{t.desc}</div>
                      <div style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>{I.gear} Setup Guide {I.ext}</div>
                    </div>
                  </a>
                ))}
              </div>

              <div style={{ marginTop: 24, padding: 20, borderRadius: 12, background: 'var(--bg-card)', border: '1px solid var(--bg-card-border)', textAlign: 'center' }}>
                <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 12 }}>Full documentation with step-by-step setup guides, configuration examples, and command references.</div>
                <a href="/docs" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 24px', borderRadius: 8, background: 'var(--accent)', color: '#000', fontWeight: 600, fontSize: 13, textDecoration: 'none' }}>{I.book} Open Documentation {I.ext}</a>
              </div>
            </>
            );
          })()}

        </div>
      </div>
    </div>
  );
};

export default Dashboard;
